import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
  Card,
  CardContent,
  Chip,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import CloudinaryService from '../../services/CloudinaryService';
import AccountSetup from './steps/AccountSetup';
import RoleSelection from './steps/RoleSelection';
import JobSeekerKYC from './steps/JobSeekerKYC';
import EmployerKYC from './steps/EmployerKYC';
// Removed unused step imports

const SmartWizard = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [targetRole, setTargetRole] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [formData, setFormData] = useState({});
  
  const { currentUser, userProfile, hasCapability, updateUserProfile, toggleUserCapability, updatePrimaryRole, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Get target role and return URL from location state
  const returnTo = location.state?.returnTo || '/';
  const message = location.state?.message || '';
  const goalRole = location.state?.targetRole || '';

  const analyzeMissingInformation = useCallback((role) => {
    const missing = [];
    // Prefer normalized employer profile when role is employer
    const profile = (role === 'employer' || role === 'job_poster')
      ? { ...normalizeEmployerFromBackend(currentUser), ...formData }
      : (userProfile || {});

    if (role === 'employer' || role === 'job_poster') {
      // Check employer-specific requirements (including service management)
      if (!profile.companyName) missing.push('companyName');
      if (!profile.industryType) missing.push('industryType');
      if (!profile.companySize) missing.push('companySize');
      if (!profile.officeAddress) missing.push('officeAddress');
      if (!profile.companyPhone) missing.push('companyPhone');
      if (!profile.companyRegNumber) missing.push('companyRegNumber');
      if (!profile.companyEmail) missing.push('companyEmail');
      if (!profile.representativeName) missing.push('representativeName');
      if (!profile.representativePosition) missing.push('representativePosition');
      if (!profile.representativePhone) missing.push('representativePhone');
      if (!profile.representativeEmail) missing.push('representativeEmail');
      // Service-related fields for employers

    }

    if (role === 'job_seeker' || role === 'job_applicant') {
      // Check job seeker-specific requirements
      if (!profile.education || profile.education.length === 0) missing.push('education');
      if (!profile.workExperience || profile.workExperience.length === 0) missing.push('workExperience');
      if (!profile.skills || profile.skills.length === 0) missing.push('skills');
      if (!profile.desiredJobRole) missing.push('desiredJobRole');
      if (!profile.employmentType) missing.push('employmentType');
      // Personal info requirements apply only to job seekers
      if (!profile.firstName) missing.push('firstName');
      if (!profile.lastName) missing.push('lastName');
      if (!profile.phone) missing.push('phone');
      if (!profile.address) missing.push('address');
    }

    // Note: no common personal info requirements for employer flow

    setMissingFields(missing);
    setFormData({
      ...profile,
      userType: role,
      ...missing.reduce((acc, field) => ({ ...acc, [field]: '' }), {})
    });
    return missing;
  }, [currentUser, userProfile, formData]);

  useEffect(() => {
    let role = goalRole;
    
    // If no goalRole from state, determine from URL path
    if (!role) {
      const path = location.pathname;
      if (path.includes('/wizard/employer')) {
        role = 'employer';
      } else if (path.includes('/wizard/job-seeker')) {
        role = 'job_seeker';
      }
    }
    
    if (role) {
      setTargetRole(role);
      analyzeMissingInformation(role);
    }
  }, [goalRole, currentUser, userProfile, location.pathname, analyzeMissingInformation]);

  // Normalize employer profile received from backend into wizard-friendly flat fields
  const normalizeEmployerFromBackend = (userLike) => {
    if (!userLike) return {};
    const ep = userLike.employerProfile || {};
    return {
      companyName: ep.companyName || '',
      industryType: ep.industry || '',
      companySize: ep.companySize || '',
      officeAddress: ep.companyAddress?.address || '',
      companyPhone: ep.contactInfo?.phone || '',
      companyEmail: ep.contactInfo?.email || '',
      companyRegNumber: ep.companyRegNumber || '',
      website: ep.website || '',
      companyDescription: ep.companyDescription || '',
      companyLogo: ep.companyLogo || '',
      representativeName: ep.representative?.name || '',
      representativePosition: ep.representative?.position || '',
      representativePhone: ep.representative?.phone || '',
      representativeEmail: ep.representative?.email || '',
      jobCategories: Array.isArray(ep.jobCategories) ? ep.jobCategories : [],
      recruitmentTeam: Array.isArray(ep.recruitmentTeam) ? ep.recruitmentTeam : [],
      // Document sets are not fetched individually here; verificationDocuments already saved as flat URLs
    };
  };


  const getSmartSteps = () => {
    const steps = [];

    // If no role selected yet, start with role selection
    if (!targetRole) {
      steps.push('Select Role');
    }

    // If user is not logged in, include credentials setup
    if (!currentUser) {
      steps.push('Account Setup');
    }

    if (missingFields.length === 0) {
      // If nothing missing beyond credentials/role, allow confirmation
      steps.push('Confirmation');
      return steps;
    }

    // Personal information step
    const personalFields = ['firstName', 'lastName', 'phone', 'address', 'dateOfBirth'];
    if (missingFields.some(field => personalFields.includes(field))) {
      steps.push('Personal Information');
    }

    // Role-specific steps
    if (targetRole === 'employer' || targetRole === 'job_poster') {
      const employerFields = ['companyName', 'industryType', 'companySize', 'officeAddress', 'companyPhone', 'companyRegNumber', 'companyEmail'];
      const representativeFields = ['representativeName', 'representativePosition', 'representativePhone', 'representativeEmail'];

      if (missingFields.some(field => employerFields.includes(field))) {
        steps.push('Company Details');
      }
      if (missingFields.some(field => representativeFields.includes(field))) {
        steps.push('Representative Information');
      }
    }

    if (targetRole === 'job_seeker' || targetRole === 'job_applicant') {
      const educationFields = ['education'];
      const experienceFields = ['workExperience'];
      const skillsFields = ['skills', 'desiredJobRole'];
      const preferencesFields = ['employmentType', 'expectedSalary', 'preferredLocations'];
      
      if (missingFields.some(field => educationFields.includes(field))) {
        steps.push('Education & Experience');
      }
      if (missingFields.some(field => experienceFields.includes(field))) {
        steps.push('Work Experience');
      }
      if (missingFields.some(field => skillsFields.includes(field))) {
        steps.push('Skills & Preferences');
      }
      if (missingFields.some(field => preferencesFields.includes(field))) {
        steps.push('Job Preferences');
      }
    }

    steps.push('Review & Complete');
    return steps;
  };

  const computeStepsFrom = (role, missing) => {
    const steps = [];
    const effectiveRole = role || targetRole;
    const mf = Array.isArray(missing) ? missing : missingFields;
    if (!effectiveRole) {
      steps.push('Select Role');
    }
    if (!currentUser) {
      steps.push('Account Setup');
    }
    if (mf.length === 0) {
      steps.push('Confirmation');
      return steps;
    }
    const personalFields = ['firstName', 'lastName', 'phone', 'address', 'dateOfBirth'];
    if (mf.some(field => personalFields.includes(field))) {
      steps.push('Personal Information');
    }
    if (effectiveRole === 'employer' || effectiveRole === 'job_poster') {
      const employerFields = ['companyName', 'industryType', 'companySize', 'officeAddress', 'companyPhone', 'companyRegNumber', 'companyEmail'];
      const representativeFields = ['representativeName', 'representativePosition', 'representativePhone', 'representativeEmail'];
      if (mf.some(field => employerFields.includes(field))) {
        steps.push('Company Details');
      }
      if (mf.some(field => representativeFields.includes(field))) {
        steps.push('Representative Information');
      }
    }
    if (effectiveRole === 'job_seeker' || effectiveRole === 'job_applicant') {
      const educationFields = ['education'];
      const experienceFields = ['workExperience'];
      const skillsFields = ['skills', 'desiredJobRole'];
      const preferencesFields = ['employmentType', 'expectedSalary', 'preferredLocations'];
      if (mf.some(field => educationFields.includes(field))) {
        steps.push('Education & Experience');
      }
      if (mf.some(field => experienceFields.includes(field))) {
        steps.push('Work Experience');
      }
      if (mf.some(field => skillsFields.includes(field))) {
        steps.push('Skills & Preferences');
      }
      if (mf.some(field => preferencesFields.includes(field))) {
        steps.push('Job Preferences');
      }
    }
    steps.push('Review & Complete');
    return steps;
  };

  const updateFormData = (newData) => {
    setFormData(prev => ({ ...prev, ...newData }));
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      
      // Prepare payload: upload documents to Cloudinary and avoid sending file objects directly
      const { documents, ...cleanData } = formData || {};
      let uploadedDocs = {};
      if (documents) {
        uploadedDocs = await uploadWizardDocuments(documents);
      }

      // Employer-specific uploads: companyLogo and verification documents
      const employerUploads = await uploadEmployerAssets(cleanData);

      const payloadWithDocs = {
        ...cleanData,
        // Job seeker assets
        resume: uploadedDocs.cvUrl || cleanData.resume,
        profilePicture: uploadedDocs.profilePhotoUrl || cleanData.profilePicture,
        certificates: uploadedDocs.certificates || cleanData.certificates,
        portfolio: uploadedDocs.portfolio || cleanData.portfolio,
        // Employer assets
        companyLogo: employerUploads.companyLogoUrl || cleanData.companyLogo,
        // Flattened verification document URLs for mapping
        businessLicense: employerUploads.documents.businessLicense,
        companyRegistration: employerUploads.documents.companyRegistration,
        taxCertificate: employerUploads.documents.taxCertificate,
        otherDocuments: employerUploads.documents.otherDocuments,
      };

      // Update user profile with new information
      await updateUserProfile(payloadWithDocs);

      // Enable job seeker capabilities and set primary role when applicable
      if (targetRole === 'job_seeker' || targetRole === 'job_applicant') {
        if (!hasCapability('canApplyForJobs')) {
          await toggleUserCapability('canApplyForJobs');
        }
        await updatePrimaryRole('jobseeker');
      } else if (targetRole === 'employer') {
        // Enable employer capability and set primary role
        if (!hasCapability('canPostJobs')) {
          await toggleUserCapability('canPostJobs');
        }
        await updatePrimaryRole('employer');
      }
      
      // Show success feedback locally, then navigate after a short delay
      setSuccess('Profile updated successfully. Redirecting...');
      setTimeout(() => {
        navigate(returnTo, { 
          state: { 
            message: 'Profile updated successfully! You can now proceed.',
            severity: 'success'
          }
        });
      }, 1200);
    } catch (err) {
      console.error('Profile update error:', err);
      setError('Failed to update profile. ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = async ({ primaryRole }) => {
    const role = primaryRole === 'job_seeker' ? 'job_seeker' : 'employer';
    setTargetRole(role);
    analyzeMissingInformation(role);
    // After role selection, the step list changes (removes 'Select Role').
    // Reset to the first step to avoid skipping 'Account Setup'.
    setActiveStep(0);
  };

  const handleCredentialsNext = async () => {
    try {
      setError('');
      setLoading(true);
      const name = `${formData.firstName || ''} ${formData.lastName || ''}`.trim();
      if (!targetRole) {
        setError('Please select a role (Jobseeker or Employer) before creating your account.');
        setLoading(false);
        return;
      }
      const selectedRoleForRegister = targetRole === 'job_seeker' ? 'jobseeker' : 'employer';
      const normalizedPhone = (formData.phone || '').replace(/[\s-]/g, '');
      const emailInput = (formData.email || '').trim().toLowerCase();
      await register(emailInput, formData.password, name, selectedRoleForRegister, normalizedPhone, {});
      setSuccess('Account created successfully');
      const missing = analyzeMissingInformation(targetRole) || [];
      const steps = computeStepsFrom(targetRole, missing);
      const accountIdx = steps.indexOf('Account Setup');
      const nextIdx = accountIdx >= 0 ? accountIdx + 1 : 0;
      setActiveStep(nextIdx);
    } catch (e) {
      console.error('Registration failed:', e);
      const msg = e?.response?.data?.message || e.message || 'Registration failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step) => {
    const steps = getSmartSteps();
    const stepName = steps[step];
    
    if (missingFields.length === 0 && stepName === 'Confirmation') {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" gutterBottom>
            Profile Complete!
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Your profile already has all the required information for this action.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(returnTo)}
            sx={{ mt: 2 }}
          >
            Continue
          </Button>
        </Box>
      );
    }
    
    switch (stepName) {
      case 'Select Role':
        return (
          <RoleSelection
            onRoleSelect={handleRoleSelect}
            onBack={() => navigate(returnTo)}
          />
        );
      case 'Account Setup':
        return (
          <AccountSetup
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleCredentialsNext}
            loading={loading}
          />
        );
      case 'Personal Information':
        return (
          <JobSeekerKYC
            step="personal"
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
            loading={loading}
            missingFields={missingFields}
          />
        );
      case 'Company Details':
        return (
          <EmployerKYC
            step="company"
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
            loading={loading}
            missingFields={missingFields}
          />
        );
      case 'Representative Information':
        return (
          <EmployerKYC
            step="representative"
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
            loading={loading}
            missingFields={missingFields}
          />
        );
      
      case 'Education & Experience':
        return (
          <JobSeekerKYC
            step="education"
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
            loading={loading}
            missingFields={missingFields}
          />
        );
      case 'Work Experience':
        return (
          <JobSeekerKYC
            step="experience"
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
            loading={loading}
            missingFields={missingFields}
          />
        );
      case 'Skills & Preferences':
        return (
          <JobSeekerKYC
            step="skills"
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
            loading={loading}
            missingFields={missingFields}
          />
        );
      case 'Job Preferences':
        return (
          <JobSeekerKYC
            step="preferences"
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
            loading={loading}
            missingFields={missingFields}
          />
        );
      case 'Review & Complete':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Your Information
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please review the information you've provided before completing your profile.
            </Typography>
            
            {/* Display the collected information */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Updated Information
                </Typography>
                {Object.entries(formData)
                  .filter(([key, value]) => missingFields.includes(key) && value)
                  .map(([key, value]) => (
                    <Box key={key} sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        <strong>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</strong> {
                          Array.isArray(value) ? value.join(', ') : value.toString()
                        }
                      </Typography>
                    </Box>
                  ))}
              </CardContent>
            </Card>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button onClick={handleBack} disabled={loading}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Complete Profile'}
              </Button>
            </Box>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  async function uploadWizardDocuments(documents) {
    const result = {
      cvUrl: '',
      profilePhotoUrl: '',
      certificates: [],
      portfolio: []
    };
    try {
      if (documents.cv?.file) {
        const up = await CloudinaryService.uploadFile(documents.cv.file, 'jobit/cv');
        result.cvUrl = up.url;
      }
      if (documents.profilePhoto?.file) {
        const up = await CloudinaryService.uploadFile(documents.profilePhoto.file, 'jobit/profile_photos');
        result.profilePhotoUrl = up.url;
      }
      if (Array.isArray(documents.certificates)) {
        for (const cert of documents.certificates) {
          if (cert?.file) {
            const up = await CloudinaryService.uploadFile(cert.file, 'jobit/certificates');
            result.certificates.push(up.url);
          }
        }
      }
      if (Array.isArray(documents.portfolio)) {
        for (const itm of documents.portfolio) {
          if (itm?.file) {
            const up = await CloudinaryService.uploadFile(itm.file, 'jobit/portfolio');
            result.portfolio.push(up.url);
          }
        }
      }
    } catch (e) {
      console.warn('Cloudinary upload error during wizard:', e);
    }
    return result;
  }

  async function uploadEmployerAssets(data = {}) {
    const result = {
      companyLogoUrl: '',
      documents: {
        businessLicense: [],
        companyRegistration: [],
        taxCertificate: [],
        otherDocuments: []
      }
    };
    try {
      // Upload company logo if it's a File
      const logo = data.companyLogo;
      if (logo && typeof logo === 'object' && logo.name) {
        try {
          const up = await CloudinaryService.uploadFile(logo, 'jobit/company_logos');
          result.companyLogoUrl = up.url;
        } catch (e) {
          console.warn('Cloudinary upload error for company logo:', e);
        }
      }

      // Helper to upload an array of file descriptor objects
      const uploadDocArray = async (arr, folder) => {
        const urls = [];
        if (Array.isArray(arr)) {
          for (const item of arr) {
            // If already a URL/string, keep it
            if (typeof item === 'string') {
              urls.push(item);
              continue;
            }
            const file = item?.file || item;
            if (file && file.name) {
              try {
                const up = await CloudinaryService.uploadFile(file, folder);
                urls.push(up.url);
              } catch (e) {
                console.warn(`Upload error for ${folder}:`, e);
              }
            }
          }
        }
        return urls;
      };

      result.documents.businessLicense = await uploadDocArray(data.businessLicense, 'jobit/employer_docs/business_license');
      result.documents.companyRegistration = await uploadDocArray(data.companyRegistration, 'jobit/employer_docs/company_registration');
      result.documents.taxCertificate = await uploadDocArray(data.taxCertificate, 'jobit/employer_docs/tax_certificate');
      result.documents.otherDocuments = await uploadDocArray(data.otherDocuments, 'jobit/employer_docs/other');
    } catch (e) {
      console.warn('Employer asset upload error during wizard:', e);
    }
    return result;
  }

  return (
    <Grid container justifyContent="center" sx={{ mt: 4, mb: 4 }}>
      <Grid item xs={12} sm={10} md={8} lg={6}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Complete Your Profile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {message || 'We need a few more details to enable this feature'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {missingFields.length > 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Missing information: {missingFields.length} field(s)
              </Typography>
              <Box sx={{ mt: 1 }}>
                {missingFields.slice(0, 5).map(field => (
                  <Chip 
                    key={field} 
                    label={field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} 
                    size="small" 
                    sx={{ mr: 1, mb: 1 }} 
                  />
                ))}
                {missingFields.length > 5 && (
                  <Chip label={`+${missingFields.length - 5} more`} size="small" />
                )}
              </Box>
            </Alert>
          )}

          <Stepper
            activeStep={activeStep}
            sx={{ mb: 4, '& .MuiStepLabel-label': { whiteSpace: 'normal', wordBreak: 'break-word', textAlign: 'center' } }}
            alternativeLabel={!isSmallScreen}
            orientation={isSmallScreen ? 'vertical' : 'horizontal'}
          >
            {getSmartSteps().map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {getStepContent(activeStep)}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default SmartWizard;